import { Box } from "@mui/system";
import { Typography, useTheme } from "@mui/material";
import { t } from "@lingui/macro";
import CustomStepper from "../AthleteOnboarding/CustomStepper";
import Loader from "@/components/Quiz/Results/Loader";
import { ErrorBoundary, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";
import useQuiz from "./useQuiz";
import { WfButton } from "@WahooFitness/redesignr";
import WfActionGroup from "@WahooFitness/redesignr/dist/Actions/WfActionGroup/WfActionGroup.js";

type QuizProps = Pick<
  ReturnType<typeof useQuiz>,
  | "activeStep"
  | "steps"
  | "handleNext"
  | "handleBack"
  | "currentStepHasValue"
  | "currentStepValid"
  | "quizLoading"
  | "quizError"
  | "buttonName"
>;

function Quiz({
  activeStep,
  steps,
  handleNext,
  handleBack,
  currentStepHasValue,
  currentStepValid,
  quizLoading,
  quizError,
  buttonName,
}: QuizProps) {
  const { palette } = useTheme();

  if (!steps) {
    return;
  }

  return (
    <>
      <GlobalHeader backAction={handleBack} />
      <ErrorBoundary
        isLoading={quizLoading}
        error={quizError}
        loadingComponent={<Loader message={t`Organizing the minions`} />}
      >
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          sx={{ boxShadow: `0 0 0 100vmax ${palette.background.level1}` }}
        >
          <>
            {/* In flow, not absolute: an absolutely positioned stepper with a solid
                background paints over the headline whenever it is taller than the
                content's fixed top padding (e.g. a 12-step quiz on a narrow screen). */}
            <Box
              display="flex"
              width="100%"
              pl={2}
              py={1}
              sx={{ backgroundColor: palette.background.level1 }}
            >
              <CustomStepper steps={steps} activeStep={activeStep} />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              flexGrow={1}
              sx={{ backgroundColor: palette.background.level1 }}
              pb="calc(var(--wf-bottom-safe-area, 0px) + 16px)"
            >
              <Box display="flex" flexDirection="column" px={2} pt={2} rowGap={1.5}>
                <Typography variant="ui-4xl-bold">{steps[activeStep].headline}</Typography>
                <Typography variant="prose-base" color="textSecondary">
                  {steps[activeStep].subheader}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center" pt={2} width="100%" px={2}>
                {steps[activeStep].component}
              </Box>
            </Box>
          </>
        </Box>
        <WfActionGroup orientation="vertical" fullWidth sticky css={{ zIndex: 1 }}>
          <WfButton
            hierarchy="primary"
            size="large"
            fullWidth
            onClick={handleNext}
            disabled={!currentStepHasValue || !currentStepValid}
          >
            {buttonName}
          </WfButton>
        </WfActionGroup>
      </ErrorBoundary>
    </>
  );
}

export default Quiz;
